/**
 * Marketing Attribution
 *
 * Captures and persists marketing context (click IDs, UTMs, referrer, landing page)
 * so every conversion can be enriched server- and client-side.
 *
 * Storage strategy:
 *   - first-touch  → localStorage  (immutable for the visitor's lifetime)
 *   - last-touch   → sessionStorage (overwritten each new session/UTM)
 *
 * No PII is stored here. PII for Enhanced Conversions is handled separately
 * in src/lib/enhancedConversions.ts and is always hashed before transport.
 */

import { setFirstPartyCookie, getCookie, captureGclid, captureFbclid } from './firstPartyCookies';

const FIRST_TOUCH_KEY = 'obc_first_touch';
const LAST_TOUCH_KEY = 'obc_last_touch';

const CLICK_ID_PARAMS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid'] as const;
const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
] as const;

export interface AttributionSnapshot {
  // Click IDs
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  msclkid?: string;
  ttclid?: string;
  // UTMs
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  utm_id?: string;
  // Context
  landing_page?: string;
  referrer?: string;
  timestamp?: string;
}

function readUrlParams(): AttributionSnapshot {
  const out: AttributionSnapshot = {};
  if (typeof window === 'undefined') return out;
  try {
    const params = new URLSearchParams(window.location.search);
    for (const k of CLICK_ID_PARAMS) {
      const v = params.get(k);
      if (v) (out as Record<string, string>)[k] = v;
    }
    for (const k of UTM_PARAMS) {
      const v = params.get(k);
      if (v) (out as Record<string, string>)[k] = v;
    }
  } catch {
    /* ignore */
  }
  return out;
}

function readStore(key: string, storage: Storage | null): AttributionSnapshot | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    return raw ? (JSON.parse(raw) as AttributionSnapshot) : null;
  } catch {
    return null;
  }
}

function writeStore(key: string, storage: Storage | null, data: AttributionSnapshot) {
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(data));
  } catch {
    /* ignore quota */
  }
}

/**
 * Capture attribution on every navigation/page load.
 * - First-touch is written ONCE.
 * - Last-touch is overwritten whenever new attribution params are present.
 * - Persists Google click IDs into first-party cookies for native gtag/Ads use.
 */
export function captureAttribution(): AttributionSnapshot {
  if (typeof window === 'undefined') return {};

  const incoming = readUrlParams();
  const now = new Date().toISOString();
  const ctx: AttributionSnapshot = {
    ...incoming,
    landing_page: window.location.pathname + window.location.search,
    referrer: document.referrer || undefined,
    timestamp: now,
  };

  // Persist Google click IDs as first-party cookies (90d)
  if (incoming.gclid) {
    captureGclid();
    setFirstPartyCookie('_gcl_gb', incoming.gbraid || '', 90);
  }
  if (incoming.gbraid) setFirstPartyCookie('_gcl_gb', incoming.gbraid, 90);
  if (incoming.wbraid) setFirstPartyCookie('_gcl_wb', incoming.wbraid, 90);
  if (incoming.fbclid) captureFbclid();

  // First touch (set once, never overwrite)
  const ls = typeof localStorage !== 'undefined' ? localStorage : null;
  const ss = typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  const existingFirst = readStore(FIRST_TOUCH_KEY, ls);
  if (!existingFirst) {
    writeStore(FIRST_TOUCH_KEY, ls, ctx);
  }

  // Last touch — update only if there is actual attribution data this hit
  const hasAttribution =
    Object.keys(incoming).length > 0 || !readStore(LAST_TOUCH_KEY, ss);
  if (hasAttribution) {
    writeStore(LAST_TOUCH_KEY, ss, ctx);
  }

  return ctx;
}

export function getStoredAttribution(): {
  firstTouch: AttributionSnapshot | null;
  lastTouch: AttributionSnapshot | null;
} {
  if (typeof window === 'undefined') return { firstTouch: null, lastTouch: null };
  return {
    firstTouch: readStore(FIRST_TOUCH_KEY, localStorage),
    lastTouch: readStore(LAST_TOUCH_KEY, sessionStorage),
  };
}

export function getGoogleClickIds(): {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
} {
  const { lastTouch, firstTouch } = getStoredAttribution();
  const merged = { ...(firstTouch || {}), ...(lastTouch || {}) };
  // Also accept raw cookies as a fallback (e.g. when storage was cleared)
  const gclid = merged.gclid || getCookie('_gcl_aw')?.split('.').slice(2).join('.') || undefined;
  const gbraid = merged.gbraid || getCookie('_gcl_gb') || undefined;
  const wbraid = merged.wbraid || getCookie('_gcl_wb') || undefined;
  return {
    gclid: gclid || undefined,
    gbraid: gbraid || undefined,
    wbraid: wbraid || undefined,
  };
}

/**
 * Flat key/value snapshot suitable for inclusion in event params.
 * Excludes anything that could be PII — only campaign metadata.
 */
export function getAttributionForEventParams(): Record<string, string | undefined> {
  const { firstTouch, lastTouch } = getStoredAttribution();
  const ids = getGoogleClickIds();
  return {
    gclid: ids.gclid,
    gbraid: ids.gbraid,
    wbraid: ids.wbraid,
    first_utm_source: firstTouch?.utm_source,
    first_utm_medium: firstTouch?.utm_medium,
    first_utm_campaign: firstTouch?.utm_campaign,
    last_utm_source: lastTouch?.utm_source,
    last_utm_medium: lastTouch?.utm_medium,
    last_utm_campaign: lastTouch?.utm_campaign,
    last_utm_term: lastTouch?.utm_term,
    last_utm_content: lastTouch?.utm_content,
    landing_page: firstTouch?.landing_page,
    initial_referrer: firstTouch?.referrer,
    current_referrer:
      typeof document !== 'undefined' ? document.referrer || undefined : undefined,
  };
}
